import React from 'react'
import { UserInfo } from '../types'
import * as authClient from './auth-client'
import { getExpSecondsOfJwtToken } from './context-util'

type AuthContextValue = {
  user: UserInfo
  initialLoadDone: boolean
  logout: () => Promise<unknown>
  setUser: (user: UserInfo | null) => void
}

type AuthProviderProps = {
  value?: AuthContextValue
  children: React.ReactNode
}

export type LoginValues = {
  email: string
  password: string
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined)

const AuthProvider = (props: AuthProviderProps): JSX.Element => {
  const [user, setUser] = React.useState<UserInfo | null>(null)
  const [initialLoadDone, setInitialLoadDone] = React.useState(false)

  const logout = async () => {
    setUser(null)
    authClient.logout().then(() => {
      localStorage.clear()
      sessionStorage.clear()
      return Promise.resolve({ data: { ok: true }, errors: null })
    })
  }

  const isTokenExpiredOrMalformed = (jwtToken: string): boolean => {
    try {
      return getExpSecondsOfJwtToken(jwtToken) * 1000 <= Date.now()
    } catch (e) {
      return true
    }
  }

  const value = React.useMemo<AuthContextValue>(() => {
    return {
      user,
      initialLoadDone,
      logout,
      setUser,
    } as AuthContextValue
  }, [user, initialLoadDone])

  React.useEffect(() => {
    const whoAmI = async () => {
      const jwtToken = await authClient.getToken()

      if (jwtToken && !isTokenExpiredOrMalformed(jwtToken)) {
        const { data } = await authClient.me()
        if (data && data.me) {
          setUser(data.me)
        }
      }
      setInitialLoadDone(true)
    }

    whoAmI()
  }, [initialLoadDone])

  return <AuthContext.Provider value={value} {...props} />
}

const useAuth = (): AuthContextValue => {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`)
  }
  return context
}

export { AuthProvider, useAuth }
